import {
  AnalyticsApi,
  AnalyticsEvent,
  DiscoveryApi,
  FetchApi,
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { AnalyticsImplementationBlueprint } from '@backstage/plugin-app-react';

/**
 * Sends every analytics event to the `analytics` backend plugin, which stores
 * it with the signed-in user for Metabase. fetchApi attaches the Backstage
 * token, so the backend knows who the user is without trusting the body.
 */
class BackendAnalytics implements AnalyticsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  captureEvent(event: AnalyticsEvent) {
    // Analytics must never break the UI, so a failed send is only logged.
    this.send(event).catch(error =>
      // eslint-disable-next-line no-console
      console.warn('Could not record analytics event', error),
    );
  }

  private async send(event: AnalyticsEvent) {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const response = await this.fetchApi.fetch(`${baseUrl}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
      // Lets the last navigate before a tab closes still arrive.
      keepalive: true,
    });
    if (!response.ok) {
      throw new Error(`analytics backend answered ${response.status}`);
    }
  }
}

const backendAnalytics = AnalyticsImplementationBlueprint.make({
  name: 'backend',
  params: define =>
    define({
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new BackendAnalytics(discoveryApi, fetchApi),
    }),
});

export const analyticsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [backendAnalytics],
});
